import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { TextBlockView } from '@plone/volto-slate/blocks/Text';
import { DetachedTextBlockEditor } from '@plone/volto-slate/blocks/Text/DetachedTextBlockEditor';
import TextLineEdit from '@plone/volto/components/manage/TextLineEdit/TextLineEdit';
import { flattenToAppURL, isInternalURL } from '@plone/volto/helpers/Url/Url';
import { Container as SemanticContainer, Button } from 'semantic-ui-react';
import { useSelector } from 'react-redux';
import config from '@plone/volto/registry';
import { ImageInput } from '@plone/volto/components/manage/Widgets/ImageWidget';
import Breadcrumbs from '@plone/volto/components/theme/Breadcrumbs/Breadcrumbs';

const CustomImageBodyTemplate = (props) => {
  const {
    block,
    blocksConfig,
    className,
    data,
    isEditMode,
    onChangeBlock,
    properties,
  } = props;

  const dataAdapter = blocksConfig[data['@type']].dataAdapter;

  const request = useSelector((state) => state.content.subrequests[block]);
  const content = request?.data;
  const buttonLink = data?.buttonLink?.[0] ? data?.buttonLink[0]['@id'] : '';

  let renderedImage = null;
  if (data.url) {
    let Image = config.getComponent('Image').component;
    if (Image) {
      // custom image component expects item summary as src
      renderedImage = (
        <Image
          item={
            data.image_scales
              ? {
                  '@id': data.url,
                  image_field: data.image_field,
                  image_scales: data.image_scales,
                }
              : null
          }
          src={!data.image_scales ? data.url : null}
          alt=""
          loading="lazy"
          responsive={true}
        />
      );
    } else {
      // default img expects string src
      renderedImage = (
        // eslint-disable-next-line no-restricted-syntax
        <img
          src={
            isInternalURL(data.url['@id'])
              ? // Backwards compat in the case that the block is storing the full server URL
                `${flattenToAppURL(data.url['@id'])}/@@images/image`
              : data.url['@id']
          }
          alt=""
          loading="lazy"
        />
      );
    }
  }

  const customContainer = config.getComponent({ name: 'Container' }).component;

  const Container = customContainer || SemanticContainer;

  return (
    <div className={cx('block pageheader', className)}>
      {data.url ? (
        <>
          <div className="pageheader-with-image">
            <div className="pageheader-image-wrapper">{renderedImage}</div>
            <div className={cx('pageheader-description')}>
              <div
                className={cx('pageheader-text', {
                  padded: !customContainer,
                })}
              >
                <div className="title">
                  {isEditMode ? (
                    <TextLineEdit
                      {...props}
                      renderTag="h2"
                      renderClassName=""
                      fieldDataName="title"
                      properties={{ title: data.title }}
                    />
                  ) : (
                    <>{data?.title && <h2>{data?.title}</h2>}</>
                  )}
                </div>
                <div className="description">
                  {isEditMode ? (
                    <DetachedTextBlockEditor {...props} />
                  ) : (
                    <TextBlockView {...props} />
                  )}
                </div>
              </div>
            </div>
          </div>
          {props.breadcrumbPath && (
            <div className="pageheader-breadcrumbs">
              <Breadcrumbs pathname={props.breadcrumbPath} />
            </div>
          )}
          {data?.showTitle && (
            <div>
              <h1 className="documentFirstHeading title-heading pageheader-text">
                {properties.title}
              </h1>
            </div>
          )}
        </>
      ) : (
        <div>
          {props.isEditMode && (
            <>
              <ImageInput
                onChange={(id, value, item) => {
                  dataAdapter({
                    block,
                    data,
                    id: 'url',
                    onChangeBlock,
                    value,
                    content,
                    item,
                  });
                }}
                placeholderLinkInput={data.placeholder}
                block={props.block}
                id={props.block}
                objectBrowserPickerType={'image'}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
CustomImageBodyTemplate.propTypes = {
  properties: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default CustomImageBodyTemplate;
